/**
 * Ticket Types and Interfaces
 * Matches backend schema exactly
 */

export enum TicketType {
  EPIC = 'epic',
  USER_STORY = 'user_story',
  TASK = 'task',
  BUG = 'bug',
  FEATURE_REQUEST = 'feature_request',
  SUPPORT_TICKET = 'support_ticket',
  ERROR_LOG = 'error_log',
}

export enum TicketStatus {
  NEW = 'new',
  TRIAGED = 'triaged',
  IN_PROGRESS = 'in_progress',
  WAITING_CUSTOMER = 'waiting_customer',
  WAITING_INTERNAL = 'waiting_internal',
  IN_REVIEW = 'in_review',
  RESOLVED = 'resolved',
  CLOSED = 'closed',
  REOPENED = 'reopened',
  CANCELLED = 'cancelled',
  ARCHIVED = 'archived',
}

export enum TicketPriority {
  CRITICAL = 'critical',
  HIGH = 'high',
  MEDIUM = 'medium',
  LOW = 'low',
}

export interface GroupBrief {  id: string;  name: string;  group_type: string;}
export interface UserBrief {
  id: string;
  name: string;
  email: string;
  avatar_url?: string;
}

export interface TagBrief {
  id: string;
  name: string;
  color: string;
}

export interface ProjectBrief {
  id: string;
  name: string;
  key: string;
}

export interface TicketBrief {
  id: string;
  ticket_key: string;
  title: string;
  status: TicketStatus;
}

export interface TicketListItem {
  id: string;
  ticket_key: string;
  title: string;
  description?: string;
  type: TicketType;
  status: TicketStatus;
  priority: TicketPriority;
  assignee?: UserBrief;
  assigned_group?: GroupBrief;  claimed_by?: UserBrief;  claimed_at?: string;
  reporter?: UserBrief;
  tags: TagBrief[];
  story_points?: number;
  sla_due_at?: string;
  sla_triage_deadline?: string;
  sla_allocation_deadline?: string;
  sla_resolution_deadline?: string;
  sla_status?: 'ok' | 'warning' | 'breached';
  sla_stage_statuses?: {
    overall: string;
    triage: string;
    allocation: string;
    resolution: string;
    days_open: number;
    days_until_resolution?: number | null;
  };
  days_open?: number;
  days_until_resolution?: number | null;
  triaged_at?: string;
  allocated_at?: string;
  completed_at?: string;
  comment_count: number;
  has_active_bell?: boolean;
  created_at: string;
  updated_at: string;
}

export interface ActivityLogEntry {
  id: string;
  action: string;
  user?: UserBrief;
  field_name?: string;
  old_value?: string;
  new_value?: string;
  created_at: string;
}

export interface TicketDetail {
  id: string;
  ticket_key: string;
  title: string;
  description?: string;
  type: TicketType;
  status: TicketStatus;
  priority: TicketPriority;
  project: ProjectBrief;
  assignee?: UserBrief;
  assigned_group?: GroupBrief;  claimed_by?: UserBrief;  claimed_at?: string;
  reporter?: UserBrief;
  epic?: TicketBrief;
  parent?: TicketBrief;
  children: TicketBrief[];
  tags: TagBrief[];
  story_points?: number;
  original_estimate_hours?: number;
  time_spent_hours?: number;
  sla_due_at?: string;
  first_response_at?: string;
  resolved_at?: string;
  reopen_count: number;
  comment_count: number;
  watchers: UserBrief[];
  activity: ActivityLogEntry[];
  created_at: string;
  updated_at: string;
  closed_at?: string;
  last_activity_at: string;
}

export interface TicketCreate {
  title: string;
  description?: string;
  type: TicketType;
  priority?: TicketPriority;
  assignee_id?: string;
  assigned_group_id?: string;
  epic_id?: string;
  parent_id?: string;
  category?: string;
  subcategory?: string;
  story_points?: number;
  tags?: string[];
  metadata?: Record<string, any>;
}

export interface TicketUpdate {
  title?: string;
  description?: string;
  status?: TicketStatus;
  priority?: TicketPriority;
  assignee_id?: string;
  assigned_group_id?: string;
  category?: string;
  subcategory?: string;
  story_points?: number;
  resolution_comment?: string;
}

export interface CommentCreate {
  content: string;
  is_internal?: boolean;
}

export interface CommentResponse {
  id: string;
  ticket_id: string;
  author?: UserBrief;
  content: string;
  is_internal: boolean;
  is_system: boolean;
  created_at: string;
  updated_at: string;
}

export interface PaginatedTickets {
  data: TicketListItem[];
  pagination: {
    next_cursor?: string;
    has_more: boolean;
    total_count: number;
  };
}

export interface TicketFilters {
  status?: TicketStatus[];
  priority?: TicketPriority[];
  type?: TicketType[];
  assignee_id?: string;
  assigned_group_id?: string;
  reporter_id?: string;
  epic_id?: string;
  search?: string;
  sort?: string;
  limit?: number;
  cursor?: string;
}

// Type aliases for service layer compatibility
export type CreateTicketData = TicketCreate;
export type UpdateTicketData = TicketUpdate;
export type Comment = CommentResponse;
export type Ticket = TicketDetail;

// Stats interface matching backend ProjectStats schema
export interface TicketStats {
  total_tickets: number;
  open_tickets: number;
  closed_tickets: number;
  resolved_tickets?: number;
  by_status?: Record<string, number>;
  by_priority?: Record<string, number>;
  tickets_by_status?: Record<string, number>;
  tickets_by_priority?: Record<string, number>;
}
